/** Shared evidence-basis and exact observed-metric presentation. */

import type { ReactNode } from 'react';

import type { MerchantProfileView } from '../../merchant/api.js';
import type { WorkspaceCopy } from '../../merchant/copy.js';
import { formatDate } from '../../merchant/model.js';
import { researchUi } from './researchPresentation.js';

type Recommendation = NonNullable<
  MerchantProfileView['result']
>['recommendations'][number];
type ReferenceView = NonNullable<Recommendation['references']>[number];

export function isContentIdea(recommendation: Recommendation): boolean {
  return recommendation.basis !== undefined && recommendation.basis !== null;
}

export function basisLabel(
  basis: NonNullable<Recommendation['basis']>,
  copy: WorkspaceCopy,
): string {
  return basis === 'LIVE_REFERENCE'
    ? copy.line.basisLive
    : basis === 'SAVED_REFERENCE'
      ? copy.line.basisSaved
      : copy.line.basisBusiness;
}

function metricLabel(
  measurement: NonNullable<ReferenceView['snapshot']>['measurements'][number],
  copy: WorkspaceCopy,
  language: 'en' | 'ar',
): string | null {
  const label =
    measurement.name === 'PLAYS' && measurement.semantics === 'INSTAGRAM_ONLY_PLAYS'
      ? copy.line.metricPlays
      : measurement.name === 'VIEWS' &&
          measurement.semantics === 'INSTAGRAM_FACEBOOK_COMBINED_VIEWS'
        ? copy.line.metricViews
        : measurement.name === 'LIKES' && measurement.semantics === 'PUBLIC_LIKES'
          ? copy.line.metricLikes
          : measurement.name === 'COMMENTS' &&
              measurement.semantics === 'PUBLIC_COMMENTS'
            ? copy.line.metricComments
            : null;
  if (label === null) return null;
  return measurement.status !== 'OBSERVED' || measurement.value === null
    ? `${label}: ${researchUi[language].unavailable}`
    : `${measurement.value.toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US')} ${label}`;
}

export function ReferenceMetrics({
  reference,
  copy,
  language,
}: {
  readonly reference: ReferenceView;
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
}): ReactNode {
  const snapshot = reference.snapshot;
  const labels =
    snapshot === null
      ? []
      : snapshot.measurements
          .map((measurement) => metricLabel(measurement, copy, language))
          .filter((label): label is string => label !== null);
  if (snapshot === null || labels.length === 0) {
    return (
      <span className="mj-ws-muted">
        {snapshot?.measurements.some(
          (metric) => metric.status === 'OBSERVED' && metric.value !== null,
        )
          ? researchUi[language].unknownMetrics
          : copy.line.metricsUnavailable}
      </span>
    );
  }
  const measuredOn =
    snapshot.observedAt === null ||
    !snapshot.measurements.some(
      (metric) => metric.status === 'OBSERVED' && metric.value !== null,
    )
      ? ''
      : ` · ${copy.line.measuredOn} ${formatDate(snapshot.observedAt, language)}`;
  return (
    <span>
      {labels.join(' · ')}
      {measuredOn}
    </span>
  );
}
